import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { Modal, Input, Typography, Tag, Tooltip } from 'antd';
import {
    AppstoreOutlined,
    SearchOutlined,
    CloseOutlined,
} from '@ant-design/icons';
import Styled from 'styled-components';

const { Title, Text } = Typography;

const HeaderLauncherTrigger = Styled.button`
  width: fit-content;
  height: 36px;
  border: 1px solid #09035bf7;
  border-radius: 10px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: #09035bf7;
  color: #ffffff;
  cursor: pointer;
  padding-inline: 10px;
  margin-left: 12px;
  transition: all 0.2s ease;

  svg, .anticon {
    font-size: 16px;
    margin-right: 2px;
    color: #ffffff;
  }

  &:hover {
    background: #09035bf7;
    border-color: #F58826;
  }

  @media (max-width: 576px) {
    width: 32px;
    height: 32px;
    margin-left: 8px;

    svg, .anticon {
      font-size: 18px;
    }
    .menu-name {
      display: none;
    }
  }
`;

/* GLASSMORPHISM POPUP MODAL TILE CARD */
const GlassAppTile = Styled.button`
  background: ${props => props.$active
    ? 'rgba(23, 90, 160, 0.12)'
    : 'rgba(255, 255, 255, 0.45)'};
  backdrop-filter: blur(16px);
  -webkit-backdrop-filter: blur(16px);
  color: #1E293B;
  border: ${props => props.$active ? '2px solid #175AA0' : '1px solid rgba(255, 255, 255, 0.7)'};
  border-radius: 16px;
  padding: 10px 8px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  cursor: pointer;
  position: relative;
  font: inherit;
  transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1);
  box-shadow: ${props => props.$active
    ? '0 6px 18px rgba(23, 90, 160, 0.18)'
    : '0 4px 12px rgba(0, 0, 0, 0.04)'};

  &:hover {
    background: rgba(255, 255, 255, 0.88);
    border-color: #175AA0;
    box-shadow: 0 8px 22px rgba(23, 90, 160, 0.22);
  }

  img {
    width: 40px;
    height: 40px;
    object-fit: contain;
    margin-bottom: 4px;
    filter: none !important;
    transition: opacity 0.25s ease;
  }

  &:hover img {
    opacity: 0.95;
  }

  .app-name {
    font-size: 12px;
    font-weight: 700;
    color: #1E293B;
    line-height: 1.2;
  }

  @media (max-width: 480px) {
    padding: 8px 6px;

    img {
      width: 34px;
      height: 34px;
      margin-bottom: 4px;
    }

    .app-name {
      font-size: 11px;
    }
  }
`;

const GlassAppGrid = Styled.div`
  display: grid;
  grid-template-columns: repeat(5, 1fr);
  gap: 10px;

  @media (max-width: 992px) {
    grid-template-columns: repeat(4, 1fr);
    gap: 10px;
  }

  @media (max-width: 768px) {
    grid-template-columns: repeat(3, 1fr);
    gap: 8px;
  }

  @media (max-width: 480px) {
    grid-template-columns: repeat(2, 1fr);
    gap: 8px;
  }
`;

/* TRANSPARENT GLASSMORPHISM MODAL CONTAINER OVERRIDING ANT DESIGN DEFAULT WHITE BG */
const GlassModalWrapper = Styled(Modal)`
  .ant-modal-content {
    background: rgba(255, 255, 255, 0.48) !important;
    backdrop-filter: blur(28px) saturate(180%) !important;
    -webkit-backdrop-filter: blur(28px) saturate(180%) !important;
    border: 1px solid rgba(255, 255, 255, 0.7) !important;
    border-radius: 24px !important;
    box-shadow: 0 24px 60px rgba(17, 96, 169, 0.25) !important;
    padding: 24px 20px !important;
  }

  .ant-modal-header {
    background: transparent !important;
  }

  .ant-modal-body {
    padding: 0 !important;
    background: transparent !important;
  }

  @media (max-width: 768px) {
    max-width: calc(100vw - 24px) !important;
    margin: 0 auto !important;

    .ant-modal-content {
      padding: 18px 14px !important;
      border-radius: 20px !important;
    }
  }
`;

const SidebarMenu = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const [isOpen, setIsOpen] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');

    const loginDetails = useSelector(state => state.auth.loginData);
    const menuItems = Array.isArray(loginDetails?.portalIcon?.asm) ? loginDetails.portalIcon.asm : [];

    const urlSegments = location.pathname.split('/').filter(Boolean);
    const currentModule = urlSegments[1] || urlSegments[0] || '';

    const isAppActive = (itemPath) => {
        if (!itemPath || !currentModule) return false;
        const itemSegments = String(itemPath).split('/').filter(Boolean);
        const itemModule = itemSegments[1] || itemSegments[0] || '';
        return itemModule.toLowerCase() === currentModule.toLowerCase();
    };

    const filteredItems = menuItems.filter(item =>
        String(item.name || '').toLowerCase().includes(searchQuery.toLowerCase())
    );

    const handleSelectApp = (path) => {
        setIsOpen(false);
        navigate(path);
    };

    return (
        <>
            <Tooltip title="ASM Modules & Apps" placement="bottom">
                <HeaderLauncherTrigger type="button" aria-label="Open ASM modules" onClick={() => setIsOpen(true)}>
                    <AppstoreOutlined /> <span className='menu-name'>Menu</span>
                </HeaderLauncherTrigger>
            </Tooltip>

            {/* TRANSPARENT GLASSMORPHISM APP LAUNCHER MODAL */}
            <GlassModalWrapper
                open={isOpen}
                onCancel={() => setIsOpen(false)}
                footer={null}
                centered
                width={640}
                closeIcon={<CloseOutlined style={{ fontSize: 18, color: '#475569' }} />}
                styles={{
                    mask: {
                        background: 'rgba(15, 23, 42, 0.35)',
                        backdropFilter: 'blur(12px)',
                        WebkitBackdropFilter: 'blur(12px)',
                    },
                }}
            >
                <div style={{ marginBottom: 16 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 2, paddingRight: 32 }}>
                        <Title level={4} style={{ margin: 0, color: '#1B2559', fontWeight: 700, fontSize: 16 }}>
                            ASM Modules & Apps
                        </Title>
                        <Tag color="blue" style={{ borderRadius: 12, paddingInline: 8, fontWeight: 600, fontSize: 11, margin: 0 }}>
                            {menuItems.length} Apps
                        </Tag>
                    </div>
                    <Text type="secondary" style={{ fontSize: 12 }}>
                        Select a module to launch or switch workspaces.
                    </Text>
                </div>

                <Input
                    prefix={<SearchOutlined style={{ color: '#64748B' }} />}
                    placeholder="Search modules..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    allowClear
                    size="middle"
                    style={{
                        borderRadius: 12,
                        marginBottom: 16,
                        background: 'rgba(255, 255, 255, 0.5)',
                        backdropFilter: 'blur(12px)',
                        border: '1px solid rgba(255, 255, 255, 0.7)',
                    }}
                />

                <div style={{ maxHeight: 480, overflowY: 'auto', padding: '8px 4px 4px 4px' }}>
                    {filteredItems.length === 0 ? (
                        <div style={{ textAlign: 'center', padding: '36px 12px' }}>
                            <Text type="secondary">{menuItems.length ? `No apps matching "${searchQuery}"` : 'No modules available'}</Text>
                        </div>
                    ) : (
                        <GlassAppGrid>
                            {filteredItems.map((item, idx) => {
                                const isActive = isAppActive(item.path);
                                return (
                                    <GlassAppTile
                                        key={item.path || idx}
                                        type="button"
                                        $active={isActive}
                                        onClick={() => handleSelectApp(item.path)}
                                    >
                                        <img src={item.image} alt={item.name} />
                                        <span className="app-name">{item.name}</span>
                                    </GlassAppTile>
                                );
                            })}
                        </GlassAppGrid>
                    )}
                </div>
            </GlassModalWrapper>
        </>
    );
};

export default SidebarMenu;
